/**
 * CteCard — a single CTE binding card inside the WITH clause (`CteSection`).
 *
 * Declaration surface (name rename, remove + reference-scan confirm) is the
 * v1 CTE edit slice. Plan 10 §2.8 S14 adds the *body* surface: when the CTE
 * body is editable (editing ready, NOT a compact surface) the expanded
 * SELECT-kind body mounts with a derived child `VqeEditContext` — the SAME
 * absolute-offset composition the subquery drill uses
 * (`deriveSubqueryEditContext`) — so its inner clauses become editable in
 * place, and a focus/drill button pushes the body onto the global drill stack
 * (mirroring `SubquerySection`). Recursive CTEs are FIRST-CLASS here: the
 * recursive read-only gate is fully lifted (decision 5), so rename / remove /
 * body editing / drill all work for a recursive clause too. Recursion is a
 * reference-based per-binding fact (`binding.isRecursive`, decision 4), and a
 * recursion-badged body in the standard two-branch `UNION [ALL]` shape renders
 * its branches labelled "Anchor query" / "Recursive step" (decision 6) via a
 * direct `SetOperatorBranches` mount. Compact surfaces keep their existing
 * read-only CTE body (decision 7 — no compact CTE drill, no branch labels).
 */
import { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Maximize2 } from 'lucide-react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { Badge } from '../../../../../components/ui/badge';
import { Button } from '../../../../../components/ui/button';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { deriveSubqueryEditContext } from '../../edit/deriveSubqueryEditContext';
import { removeCte } from '../../edit/edits/removeCte';
import { useVqeStore } from '../../store/vqeStore';
import type { DrillEntry } from '../../types';
import { ConfirmDestructiveDialog } from '../../ux/ConfirmDestructiveDialog';
import { buildDrillLabel } from '../../ux/drillLabel';
import { useCteReferenceScan } from '../../ux/useCteReferenceScan';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from '../common/VqeTooltip';
import { CteEditableName } from './CteEditableName';
import { detectRecursiveBranchShape, type CteBinding } from './extractCtes';
import { SelectRenderer } from './SelectRenderer';
import { SetOperatorBranches } from './SetOperatorBranches';
import { makeNestedStatement, makeNestedSubqueryStatement } from './nestedStatement';

const TOOLTIP_DISABLED = vqeCopy.disabled.editingUnavailable;
// Interim safety belt: when the reference scan can't be produced we refuse to
// remove rather than risk a silent dangling-reference corruption.
const TOOLTIP_SCAN_UNAVAILABLE = vqeCopy.disabled.removeUnverified;
// Anchor/recursive-step labels for the standard two-branch recursive body
// (decision 6); index 0 is the anchor query, index 1 the recursive step.
const RECURSIVE_BRANCH_LABELS: readonly string[] = [vqeCopy.cte.anchorBranch, vqeCopy.cte.recursiveStep];

// COM-27 — a bare (non-recursive) SELECT-kind CTE body mounts a nested
// <SelectRenderer>, whose root shares the SAME full-editor `FULL_CANVAS_ROOT_CLASS`
// chrome used by every top-level statement panel (SELECT/INSERT/UPDATE/
// DELETE/MERGE/fallbacks all use it — see `SelectRenderer.tsx`'s `rootClass`).
// Its own `py-3` (12px) top padding, stacked under this card's `mt-2` (8px)
// body wrapper, produced a dead gap between the sticky CTE header and the
// body's first clause card (`comp-cte-oracle-full.png`, COM-27). This
// negative margin counteracts the nested renderer's fixed `py-3`, landing the
// net visible gap at the app's usual ~8px clause rhythm
// (the same unit as `gap-2`/`mt-2` elsewhere in this card and in ClauseCard).
// Scoped to the bare-SelectRenderer path only: the `<pre>`-rendered
// non-SELECT body has no such padding to counteract, and the recursive
// SetOperatorBranches path renders its own bordered per-branch cards (a
// separate spacing surface, out of COM-27's scope) — both keep `mt-2`.
const CTE_BODY_TIGHT_MARGIN = '-mt-1';

const BODY_KIND_LABEL: Record<CteBinding['bodyKind'], string> = {
  select: 'SELECT',
  insert: 'INSERT',
  update: 'UPDATE',
  delete: 'DELETE',
  merge: 'MERGE',
  unknown: 'unknown',
};

export interface CteCardProps {
  readonly bufferText: string;
  readonly binding: CteBinding;
  readonly options: ParseOptions;
  readonly statementId: string;
  readonly index: number;
  readonly cteCount: number;
  readonly editReady: boolean;
  readonly editContext?: VqeEditContext;
  readonly onEditAsSql: () => void;
  /** Tab id the CTE-body drill entry is attributed to (same fallback family as `subqueryCtx.drillTabId`). */
  readonly drillTabId?: string;
  /** Compact surfaces keep the read-only CTE body — the drill/body-edit affordances are suppressed (decision 7). */
  readonly compact?: boolean;
}

/**
 * FIX-B — the CTE card expansion key. Scoped on the STABLE `drillTabId` (the
 * workspace always threads the tab id) rather than the statement id, because
 * statement ids are CONTENT-HASHED (`ids.ts` statementId = sha1(sha1(text) +
 * startOffset)): a committed edit inside the CTE body re-ids the statement, and
 * a statement-id-scoped key would re-render every expanded card COLLAPSED after
 * each commit (unmounting the body a second successive edit needs). The
 * `statementId` fallback serves mounts without a tab scope (unit mounts —
 * `SelectRenderer` synthesises `drillTabId ?? statement.id` via `subqueryCtx`).
 * Exported so `CteBulkToggle` (CteSection.tsx) builds the SAME keys and the
 * bulk toggle stays in lock-step with the per-card chevrons. Per-tab scoping
 * also keeps e2e cross-test isolation: every harness call opens a NEW tab.
 */
export function cteExpansionKey(
  drillTabId: string | undefined,
  statementId: string,
  bindingName: string,
): string {
  return `${drillTabId ?? statementId}:${bindingName}`;
}

export function CteCard({
  bufferText, binding, options, statementId, index, cteCount,
  editReady, editContext, onEditAsSql, drillTabId, compact,
}: CteCardProps) {
  const expansionKey = cteExpansionKey(drillTabId, statementId, binding.name);
  const expanded = useVqeStore((s) => s.expansion[expansionKey] ?? false);
  const setExpansion = useVqeStore((s) => s.setExpansion);
  const pushDrill = useVqeStore((s) => s.pushDrill);
  const testId = `vqe-cte-${index}`;
  const bodyText = bufferText.slice(binding.bodyStart, binding.bodyEnd);
  const kindLabel = BODY_KIND_LABEL[binding.bodyKind];
  // Recursive CTEs are first-class (gate lift, decision 5): rename / remove /
  // body edit are gated only on edit-readiness, not recursiveness. Compact
  // surfaces still keep the read-only CTE body (decision 7).
  const cteEditEnabled = editReady && !!editContext;
  const bodyEditingEnabled = cteEditEnabled && !compact;
  // A SELECT-kind body that is editable gets the focus/drill affordance.
  const canDrillBody = bodyEditingEnabled && binding.bodyKind === 'select';
  const { track } = useAnalytics();

  // Standard anchor/recursive-step branch outline (decision 6) — non-null only
  // for a recursion-badged, non-compact, SELECT-kind body in the two-branch
  // `UNION [ALL]` shape. Memoised on the body bounds so there is no per-render
  // rescan; used both for the labelled render and its presence check.
  const recursiveBranchOutline = useMemo(
    () =>
      !compact && binding.isRecursive && binding.bodyKind === 'select'
        ? detectRecursiveBranchShape(bufferText, binding, options)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compact, binding.isRecursive, binding.bodyKind, binding.bodyStart, binding.bodyEnd, bufferText, options],
  );

  // Cross-level edit composition (§2.8 S14): a derived child edit context bounded
  // to this CTE body's byte range — the same absolute-offset mechanism the
  // subquery drill uses. `binding.bodyStart/bodyEnd` are re-extracted on every
  // render (extractCtes re-parses the current buffer), so they never go stale
  // after an inner edit — no bodyEnd re-derivation is needed here. Wrapped in
  // try/catch to fall back to the read-only body mount when the document isn't
  // open in the service yet (mirrors `useVqeTabDrill`'s drilled-context idiom).
  const bodyEditContext = useMemo<VqeEditContext | undefined>(() => {
    if (!bodyEditingEnabled || !editContext || binding.bodyKind !== 'select') return undefined;
    try {
      return deriveSubqueryEditContext(
        editContext,
        editContext.statementId,
        binding.bodyStart,
        binding.bodyEnd,
      );
    } catch {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyEditingEnabled, editContext, binding.bodyKind, binding.bodyStart, binding.bodyEnd]);

  // Synchronous reference scan (same byte walker the rename planner uses).
  const scan = useCteReferenceScan(binding, cteEditEnabled ? editContext : undefined);
  // Interim safety belt: if we couldn't produce a scan, removal is unsafe.
  const scanUsable = cteEditEnabled && !scan.scanFailed;
  const removeEnabled = scanUsable;

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Resolver for the pending confirm — fulfilled by the AlertDialog buttons.
  const confirmResolverRef = useRef<((proceed: boolean) => void) | null>(null);

  const settleConfirm = (proceed: boolean): void => {
    setConfirmOpen(false);
    const resolve = confirmResolverRef.current;
    confirmResolverRef.current = null;
    if (resolve) resolve(proceed);
  };

  const onRemoveClick = (): void => {
    if (!removeEnabled || !editContext) return;
    // Re-entry guard: a second fast click before <ConfirmDestructiveDialog>
    // paints would overwrite confirmResolverRef and leak the first
    // removeCte's confirmIfReferenced() Promise as permanently unresolved.
    if (confirmOpen) return;
    void removeCte(index, toCommitCtx(editContext), {
      cteCount,
      references: scan.references,
      confirmIfReferenced: () =>
        new Promise<boolean>((resolve) => {
          confirmResolverRef.current = resolve;
          setConfirmOpen(true);
        }),
    });
  };

  const onConfirmRemove = (): void => {
    track('feature:vqe_destructive_confirm_apply', { kind: 'cte' });
    settleConfirm(true);
  };

  const onCancelRemove = (): void => {
    track('feature:vqe_destructive_confirm_cancel', { kind: 'cte' });
    settleConfirm(false);
  };

  const drillLabel = buildDrillLabel({ kind: 'cte', identifier: binding.name, positionalIndex: index });

  const onFocusBody = (): void => {
    const entry: DrillEntry = {
      tabId: drillTabId ?? statementId,
      // `statementId` is this mount's id — synthetic for a nested mount. The
      // DrillEntry id string is label/uniqueness only: the workspace applies the
      // drilled edit range-first (re-deriving the child statementInfo from the
      // REAL top-level statement + `bodyStart`), so the id is never load-bearing.
      parentStatementId: statementId,
      bodyStart: binding.bodyStart,
      bodyEnd: binding.bodyEnd,
      label: drillLabel,
    };
    track('feature:vqe_drill', {
      depth: useVqeStore.getState().drillStack.length + 1,
      source: 'cte',
    });
    pushDrill(entry);
  };

  const removeTooltip = !cteEditEnabled
    ? TOOLTIP_DISABLED
    : !removeEnabled
      ? TOOLTIP_SCAN_UNAVAILABLE
      : scan.referenceCount > 0
        ? vqeCopy.cte.removeReferenced
        : vqeCopy.cte.remove;

  return (
    <div
      // Task cte-context (COM-05/06): `data-[cte-ref-focus=true]:*` responds to
      // the FROM-item CTE-ref badge's brief highlight (FromCteRefBadge.tsx sets
      // `data-cte-ref-focus="true"` on THIS element — found via the pre-existing
      // `data-cte-name` anchor below — then clears it after ~1.2s).
      className="rounded-md border border-border bg-muted/30 px-3 py-2 transition-shadow duration-300 data-[cte-ref-focus=true]:ring-2 data-[cte-ref-focus=true]:ring-primary/70"
      data-testid={testId}
      data-cte-name={binding.name}
      data-recursive={binding.isRecursive ? 'true' : 'false'}
      data-body-kind={binding.bodyKind}
    >
      <div
        // Task cte-context (COM-05/06): sticky header while scrolled — inside a
        // long CTE body there was no sticky context, so the CTE's name/type
        // scrolled away with everything else. CSS-only: `top-0` sticks against
        // the nearest scrolling ancestor, which is the OUTERMOST SelectRenderer
        // mount's `overflow-auto` root (nested CTE-body SelectRenderer mounts
        // also carry `overflow-auto`, but never acquire a bounded height inside
        // this card's unconstrained `mt-2` body wrapper, so they never become
        // the effective scroll container). Scoped to THIS row only — `sticky`
        // elements are bounded by their own parent's box, so once the card's
        // bottom edge (and the WITH section's `[+ add CTE]` footer below it)
        // scrolls into view the header is naturally pushed away with it; it
        // cannot permanently cover the footer.
        //
        // It reads as the card's own header row, not a bar floating over the
        // body: `-mx-3 -mt-2` spans the card's full inner width from its top
        // edge, the fill is the card's `bg-muted/30`-over-`bg-card` colour made
        // opaque (so stuck content never shows through), and an expanded card
        // gets a divider under it. `-top-3` offsets the canvas root's `py-3`
        // (FULL_CANVAS_ROOT_CLASS): sticky insets are measured inside the scroll
        // container's padding, so `top-0` left a 12px strip where rows scrolled
        // past above the stuck header.
        className="sticky -top-3 z-10 -mx-3 -mt-2 flex items-center gap-2 rounded-t-md border-b border-transparent bg-[color-mix(in_oklab,var(--color-muted)_30%,var(--color-card))] px-3 py-1.5 data-[expanded=false]:-mb-2 data-[expanded=true]:border-border"
        data-testid={`${testId}-header`}
        data-expanded={expanded ? 'true' : 'false'}
      >
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpansion(expansionKey, !expanded)}
          aria-expanded={expanded}
          data-testid={`${testId}-toggle`}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {cteEditEnabled && editContext ? (
          <CteEditableName
            testId={`${testId}-name`}
            currentName={binding.name}
            cteIndex={index}
            cteCount={cteCount}
            editContext={editContext}
          />
        ) : (
          <span className="font-mono text-sm font-medium" data-testid={`${testId}-name`}>
            {binding.name}
          </span>
        )}
        {binding.columns && binding.columns.length > 0 ? (
          <span
            className="font-mono text-[11px] text-muted-foreground"
            data-testid={`${testId}-columns`}
          >
            ({binding.columns.join(', ')})
          </span>
        ) : null}
        {binding.isRecursive ? (
          <Badge variant="outline" className="uppercase" data-testid={`${testId}-recursive`}>
            recursive
          </Badge>
        ) : null}
        <span
          className="rounded bg-background px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
          data-testid={`${testId}-body-kind`}
        >
          {kindLabel}
        </span>
        <div className="flex-1" />
        {canDrillBody ? (
          <VqeTooltip content={vqeCopy.cte.focusBody}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onFocusBody}
              className="h-7 gap-1 px-1.5 text-xs text-muted-foreground"
              data-testid={`${testId}-focus`}
              aria-label={`Focus on ${drillLabel}`}
            >
              <Maximize2 className="h-3 w-3" />
              focus
            </Button>
          </VqeTooltip>
        ) : null}
        <VqeTooltip content={removeTooltip}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!removeEnabled}
            aria-disabled={!removeEnabled}
            className="h-7 px-2 text-xs text-muted-foreground"
            data-testid={`${testId}-remove`}
            onClick={removeEnabled ? onRemoveClick : undefined}
          >
            {vqeCopy.actions.remove}
          </Button>
        </VqeTooltip>
      </div>

      <ConfirmDestructiveDialog
        open={confirmOpen}
        testId="vqe-cte-remove-confirm"
        title={vqeCopy.confirm.removeReferencedCte.title(binding.name, scan.referenceCount)}
        description={vqeCopy.confirm.removeReferencedCte.body}
        confirmLabel={vqeCopy.confirm.removeReferencedCte.apply}
        cancelLabel={vqeCopy.confirm.removeReferencedCte.cancel}
        onConfirm={onConfirmRemove}
        onCancel={onCancelRemove}
      />

      {expanded ? (
        <div
          className={
            binding.bodyKind === 'select' && !recursiveBranchOutline
              ? CTE_BODY_TIGHT_MARGIN
              : 'mt-2'
          }
          data-testid={`${testId}-body`}
        >
          {binding.bodyKind === 'select' ? (
            // Cross-level composition (§2.8 S14): when the body is editable we
            // mount it against a derived body edit context bounded to the CTE's
            // byte range (`bodyEditContext`) so its inner clauses edit in place;
            // the read-only mount (no editContext threaded) is the fallback for
            // compact / not-yet-ready surfaces. Outer FROM aliases are
            // deliberately NOT passed down — a CTE body is not correlated with the
            // enclosing statement's FROM aliases, so it needs no `outerAliases`.
            recursiveBranchOutline ? (
              // Standard recursive shape (decision 6): render the branches
              // DIRECTLY so we can label the anchor query + recursive step. This
              // mirrors SelectRenderer's own set-op path (same synthetic body
              // statement + derived body editContext it would build) — bypassing
              // it only to thread the labels SelectRenderer can't forward.
              <SetOperatorBranches
                statement={makeNestedSubqueryStatement(
                  bodyEditContext ? editContext!.statementId : statementId,
                  binding.bodyStart,
                  binding.bodyEnd,
                )}
                bufferText={bufferText}
                options={options}
                outline={recursiveBranchOutline}
                onEditAsSql={onEditAsSql}
                drillTabId={drillTabId}
                editContext={bodyEditContext}
                branchLabels={RECURSIVE_BRANCH_LABELS}
                branchLabelTestIdPrefix={testId}
              />
            ) : bodyEditContext ? (
              <SelectRenderer
                statement={makeNestedSubqueryStatement(editContext!.statementId, binding.bodyStart, binding.bodyEnd)}
                bufferText={bufferText}
                options={options}
                onEditAsSql={onEditAsSql}
                drillTabId={drillTabId}
                editContext={bodyEditContext}
              />
            ) : (
              <SelectRenderer
                statement={makeNestedStatement(statementId, binding)}
                bufferText={bufferText}
                options={options}
                onEditAsSql={onEditAsSql}
              />
            )
          ) : (
            <pre
              className="max-h-72 overflow-auto rounded-md border border-border bg-background p-3 font-mono text-xs leading-relaxed"
              data-testid={`${testId}-body-text`}
            >
              {bodyText}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
}
